import { useEffect, useState } from "react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { supabase } from "@/integrations/supabase/client";
import { recordProfile } from "@/hooks/use-session";
import { plans } from "@/data/site-content";

export const Route = createFileRoute("/account")({
  ssr: false,
  beforeLoad: async ({ location }) => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) {
      throw redirect({ to: "/auth", search: { next: location.href } });
    }
    void recordProfile(data.user);
    return { userId: data.user.id, userEmail: data.user.email ?? "" };
  },
  head: () => ({
    meta: [
      { title: "Your Account | MG Content Labs" },
      {
        name: "description",
        content:
          "Manage your MG Content Labs profile, view your subscription plan, change your password or sign out.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Your Account | MG Content Labs" },
      { property: "og:description", content: "Manage your MG Content Labs profile and subscription." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AccountPage,
});

type ProfileRow = {
  full_name: string | null;
  email: string;
  created_at: string;
  plan_slug: string | null;
  plan_mode: string | null;
  plan_selected_at: string | null;
};

function AccountPage() {
  const { userId, userEmail } = Route.useRouteContext();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<ProfileRow | null>(null);
  const [fullName, setFullName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwError, setPwError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void supabase
      .from("profiles")
      .select("full_name, email, created_at, plan_slug, plan_mode, plan_selected_at")
      .eq("id", userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || !data) return;
        setProfile(data as ProfileRow);
        setFullName(data.full_name ?? "");
      });
    return () => {
      active = false;
    };
  }, [userId]);

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    setSavingName(true);
    setNameMsg(null);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName.trim() || null })
      .eq("id", userId);
    setSavingName(false);
    setNameMsg(error ? error.message : "Saved.");
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setSavingPw(true);
    setPwMsg(null);
    setPwError(null);
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
      current_password: currentPassword,
    } as Parameters<typeof supabase.auth.updateUser>[0]);
    setSavingPw(false);
    if (error) {
      setPwError(error.message);
      return;
    }
    setCurrentPassword("");
    setNewPassword("");
    setPwMsg("Your password has been updated.");
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate({ to: "/", replace: true });
  }

  const plan = profile?.plan_slug ? plans.find((p) => p.slug === profile.plan_slug) : undefined;

  return (
    <div className="min-h-screen bg-white text-brand-deep">
      <SiteNav />

      <main className="mx-auto max-w-3xl px-6 py-16 md:py-24">
        <h1 className="font-serif text-4xl md:text-5xl">Your account</h1>
        <p className="mt-3 text-brand-deep/70">Signed in as {userEmail}</p>

        {/* Subscription */}
        <section className="mt-10 border border-brand-deep/10 p-8">
          <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">Subscription</h2>
          {plan ? (
            <div className="mt-4">
              <div className="font-serif text-2xl italic">{plan.name}</div>
              <p className="mt-2 text-sm text-brand-deep/70">
                {profile?.plan_mode === "once" ? "One-time payment" : "Billed monthly"} ·{" "}
                <span className="tabular-nums">{plan.price.inr}</span> ({plan.price.usd})
              </p>
              {profile?.plan_selected_at && (
                <p className="mt-1 text-xs text-brand-deep/50">
                  Started {new Date(profile.plan_selected_at).toLocaleDateString()}
                </p>
              )}
              <Link
                to="/pricing"
                className="mt-5 inline-block text-sm font-semibold text-brand-accent hover:underline"
              >
                Change plan →
              </Link>
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-sm text-brand-deep/70">You don&rsquo;t have a plan yet.</p>
              <Link
                to="/pricing"
                className="mt-5 inline-block bg-brand-accent px-6 py-3 text-xs font-bold uppercase tracking-widest text-white"
              >
                View plans
              </Link>
            </div>
          )}
        </section>

        {/* Profile details */}
        <section className="mt-8 border border-brand-deep/10 p-8">
          <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">Profile details</h2>
          <form onSubmit={saveName} className="mt-5 space-y-4">
            <div>
              <label htmlFor="fullName" className="text-xs uppercase tracking-widest text-brand-deep/50">
                Full name
              </label>
              <input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your name"
                className="mt-2 w-full border border-brand-deep/15 px-4 py-2.5 text-sm focus:border-brand-accent focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="accEmail" className="text-xs uppercase tracking-widest text-brand-deep/50">
                Email
              </label>
              <input
                id="accEmail"
                value={profile?.email ?? userEmail}
                readOnly
                className="mt-2 w-full border border-brand-deep/10 bg-brand-muted px-4 py-2.5 text-sm text-brand-deep/60"
              />
            </div>
            {profile?.created_at && (
              <p className="text-xs text-brand-deep/50">
                Member since {new Date(profile.created_at).toLocaleDateString()}
              </p>
            )}
            <button
              type="submit"
              disabled={savingName}
              className="bg-brand-accent px-6 py-3 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
            >
              {savingName ? "Saving…" : "Save changes"}
            </button>
            {nameMsg && <p className="text-sm text-brand-deep/70">{nameMsg}</p>}
          </form>
        </section>

        {/* Password */}
        <section className="mt-8 border border-brand-deep/10 p-8">
          <h2 className="text-xs font-bold uppercase tracking-widest text-brand-accent">Change password</h2>
          <form onSubmit={changePassword} className="mt-5 space-y-4">
            <input
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Current password"
              className="w-full border border-brand-deep/15 px-4 py-2.5 text-sm focus:border-brand-accent focus:outline-none"
            />
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              className="w-full border border-brand-deep/15 px-4 py-2.5 text-sm focus:border-brand-accent focus:outline-none"
            />
            <button
              type="submit"
              disabled={savingPw}
              className="bg-brand-accent px-6 py-3 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
            >
              {savingPw ? "Updating…" : "Update password"}
            </button>
            {pwMsg && <p className="text-sm text-brand-deep/70">{pwMsg}</p>}
            {pwError && (
              <p role="alert" className="text-sm text-red-700">
                {pwError}
              </p>
            )}
          </form>
          <p className="mt-4 text-xs text-brand-deep/50">
            Signed in with Google or Microsoft? Password changes don&rsquo;t apply to those accounts.
          </p>
        </section>

        <button
          type="button"
          onClick={handleSignOut}
          className="mt-8 border border-brand-deep/15 px-6 py-3 text-xs font-bold uppercase tracking-widest text-brand-deep transition hover:bg-brand-muted"
        >
          Log out
        </button>
      </main>

      <SiteFooter />
    </div>
  );
}
